import { type Inline } from '@atlaskit/adf-schema';

import type { JSONNode } from './JSONNode';

/**
 * Inserts URL inline card nodes into the document content.
 * If the last node is a paragraph, appends to it; otherwise creates a new paragraph.
 *
 * @param existingContent - The current document content array
 * @param newInlineContent - The new inline card nodes to add
 * @returns The updated document content with URLs inserted
 */
export const insertUrlsIntoDocument = (
	existingContent: JSONNode[],
	newInlineContent: Inline[],
): JSONNode[] => {
	// Case 1: Empty document - create the first paragraph
	if (existingContent.length === 0) {
		return [
			{
				type: 'paragraph',
				content: newInlineContent,
			},
		];
	}

	// Case 2: Document has content - check the last node
	const lastNode = existingContent[existingContent.length - 1];
	if (lastNode.type === 'paragraph') {
		// Append to existing paragraph with a space separator if needed
		const existingParagraphContent = lastNode.content || [];
		const separator: Inline[] =
			existingParagraphContent.length > 0 ? [{ type: 'text', text: ' ' }] : [];

		return [
			...existingContent.slice(0, -1),
			{
				...lastNode,
				content: [...existingParagraphContent, ...separator, ...newInlineContent],
			},
		];
	}

	// Case 3: Last node is not a paragraph - create a new paragraph
	return [
		...existingContent,
		{
			type: 'paragraph',
			content: newInlineContent,
		},
	];
};
